import React, { useState, useEffect, useCallback } from "react";
import Styles from "./CompanyDetails.module.css";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdmingeneratorRaListing,
  fetchAdminGeneratorRaListing,
  fetchgeneratorCapacities,
  fetchGeneratorCapacities,
  clearActivities,
} from "../../../Redux/Slices/gnaAdminSlice";
import { clearWalletContract } from "../../../Redux/Slices/paymentSlice";
import ListingDetails from "../../Modals/Admin/ListingDetails/ListingDetails";
import ListingTable from "./ListingTable";
import WalletTable from "./WalletTable/WalletTable";

const GeneratorDetails = ({ fullGeneratorDetails }) => {
  const dispatch = useDispatch();
  const generatorId = fullGeneratorDetails?.generatorId;

  const [viewType, setViewType] = useState("details");
  const [listingView, setListingView] = useState({
    open: false,
    data: null,
  });

  // Selectors
  const bilateralListings = useSelector(
    (state) => state.gnaAdminSlice.fetchCapacity?.data?.page,
  );

  const raListings = useSelector(
    (state) => state.gnaAdminSlice.fetchRaList?.data?.page,
  );

  /**
   * Effect to clear wallet data when generatorId changes.
   * This ensures fresh data is loaded when switching between generators.
   */
  useEffect(() => {
    dispatch(clearActivities());
    dispatch(clearWalletContract());
  }, [generatorId, dispatch]);

  /**
   * Effect for fetching data when the viewType changes.
   */
  useEffect(() => {
    if (!generatorId) return;

    // Reset previous listings data when switching view types, if necessary
    // Note: We rely on the thunks to handle loading states/fresh data in the slice.

    switch (viewType) {
      case "bilateral":
        dispatch(fetchGeneratorCapacities(generatorId));
        break;
      case "ra":
        // Fetch Auction Tenders (type: RA)
        dispatch(
          fetchAdminGeneratorRaListing({
            id: generatorId,
            type: "RA",
            companyId: fullGeneratorDetails?.companyId,
          }),
        );
        break;
      case "rfq":
        // Fetch RfQ Tenders (type: CB)
        dispatch(
          fetchAdminGeneratorRaListing({
            id: generatorId,
            type: "CB",
            companyId: fullGeneratorDetails?.companyId,
          }),
        );
        break;
      case "details":
      default:
        // No fetch needed for details view
        break;
    }
  }, [viewType, generatorId, dispatch]);

  /**
   * Helper component to render the main 'Details' section.
   */
  const renderDetails = useCallback(() => {
    const details = [
      {
        header: "Generator Full Details",
        items: [
          { label: "Generator ID", value: fullGeneratorDetails?.generatorId },
          {
            label: "Generator Name",
            value: fullGeneratorDetails?.generatorName,
          },
          {
            label: "Generator Type",
            value: fullGeneratorDetails?.specificationName,
          },
        ],
      },
      {
        header: "Bank Details",
        items: [
          {
            label: "Bank Name",
            value: fullGeneratorDetails?.generatorBankDetails?.bankName,
          },
          {
            label: "Payee Name",
            value: fullGeneratorDetails?.generatorBankDetails?.payee,
          },
          {
            label: "Bank Account Number",
            value: fullGeneratorDetails?.generatorBankDetails?.accountNumber,
          },
          {
            label: "Bank Address",
            value: fullGeneratorDetails?.generatorBankDetails?.branchAddress,
          },
          {
            label: "Wallet Balance",
            value: fullGeneratorDetails?.walletAccount?.displayAmountRemaining
              ? Number(
                  fullGeneratorDetails.walletAccount.displayAmountRemaining,
                ).toFixed(2)
              : "-",
          },
        ],
      },
      {
        header: "Office Address",
        items: [
          {
            label: "Address",
            value: fullGeneratorDetails?.generatorOfficeAddress?.address,
          },
          {
            label: "Pincode",
            value: fullGeneratorDetails?.generatorOfficeAddress?.pinCode,
          },
          {
            label: "City",
            value: fullGeneratorDetails?.generatorOfficeAddress?.cityName,
          },
          {
            label: "State",
            value: fullGeneratorDetails?.generatorOfficeAddress?.stateUtName,
          },
          {
            label: "Contact",
            value:
              fullGeneratorDetails?.generatorOfficeAddress?.contactPersonName,
          },
          {
            label: "Email",
            value: fullGeneratorDetails?.generatorOfficeAddress?.email,
          },
          {
            label: "Mobile",
            value: fullGeneratorDetails?.generatorOfficeAddress?.mobileNumber,
          },
          {
            label: "Alt. Mobile",
            value:
              fullGeneratorDetails?.generatorOfficeAddress
                ?.alternateMobileNumber,
          },
        ],
      },
      {
        header: "Correspondence Address",
        items: [
          {
            label: "Address",
            value:
              fullGeneratorDetails?.generatorCorrespondenceAddress?.address,
          },
          {
            label: "Pincode",
            value:
              fullGeneratorDetails?.generatorCorrespondenceAddress?.pinCode,
          },
          {
            label: "City",
            value:
              fullGeneratorDetails?.generatorCorrespondenceAddress?.cityName,
          },
          {
            label: "State",
            value:
              fullGeneratorDetails?.generatorCorrespondenceAddress?.stateUtName,
          },
          {
            label: "Contact",
            value:
              fullGeneratorDetails?.generatorCorrespondenceAddress
                ?.contactPersonName,
          },
          {
            label: "Email",
            value: fullGeneratorDetails?.generatorCorrespondenceAddress?.email,
          },
          {
            label: "Mobile",
            value:
              fullGeneratorDetails?.generatorCorrespondenceAddress
                ?.mobileNumber,
          },
          {
            label: "Alt. Mobile",
            value:
              fullGeneratorDetails?.generatorCorrespondenceAddress
                ?.alternateMobileNumber,
          },
        ],
      },
    ];

    return (
      <>
        {details.map((section, sectionIndex) => (
          <div
            key={sectionIndex}
            className={`display_flex_column_gap_8 ${Styles.detailsContainer}`}
          >
            <div className={Styles.sectionHeader}>{section.header}</div>
            <div className={`${Styles.detailRow}`}>
              {section.items.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  className={`display_flex_column_gap_8 ${Styles.detailItem}`}
                >
                  <div>{item.label}</div>
                  <div>{item.value || "-"}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </>
    );
  }, [fullGeneratorDetails]);

  // Helper component to render the active view content
  const renderActiveView = () => {
    switch (viewType) {
      case "details":
        return renderDetails();
      case "bilateral":
        return (
          <ListingTable
            type="bilateral"
            data={bilateralListings}
            setListingView={setListingView}
          />
        );
      case "ra":
        return (
          <ListingTable
            type="ra"
            data={raListings}
            setListingView={setListingView}
          />
        );
      case "rfq":
        return (
          <ListingTable
            type="rfq"
            data={raListings} // RfQ uses the same reducer state fetchRaList
            setListingView={setListingView}
          />
        );
      case "wallet":
        return <WalletTable fullGeneratorDetails={fullGeneratorDetails} />;
      default:
        return null;
    }
  };

  return (
    <div>
      {listingView.open && (
        <ListingDetails
          open={listingView.open}
          handleClose={() => setListingView({ open: false, data: null })}
          data={listingView.data}
        />
      )}
      <div className={`display_flex_row_center ${Styles.viewTypeContainer}`}>
        <div
          onClick={() => setViewType("details")}
          className={viewType === "details" ? `activeButton` : ""}
        >
          Details
        </div>
        <div
          onClick={() => setViewType("bilateral")}
          className={viewType === "bilateral" ? `activeButton` : ""}
        >
          Bilateral Listings
        </div>
        <div
          onClick={() => setViewType("ra")}
          className={viewType === "ra" ? `activeButton` : ""}
        >
          Auction Tenders
        </div>
        <div
          onClick={() => setViewType("rfq")}
          className={viewType === "rfq" ? `activeButton` : ""}
        >
          Rfq Tenders
        </div>
        <div
          onClick={() => setViewType("wallet")}
          className={viewType === "wallet" ? `activeButton` : ""}
        >
          Transaction Details
        </div>
      </div>
      <div className={`scroll_bar_y ${Styles.activeViewEntity}`}>
        {renderActiveView()}
      </div>
    </div>
  );
};

export default GeneratorDetails;
